import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  isSalesforceIframe,
  getSalesforceParams,
  isSalesforceAllowedPath,
} from "@/core/utils/salesforce-iframe.utils";

/**
 * Hook that enforces navigation restrictions when the app is embedded in a Salesforce iframe.
 *
 * When Salesforce query parameters are detected, this hook:
 * - Allows navigation only to: /login, /sign-up, /pages/integrations/salesforce
 * - Redirects authenticated users to /pages/integrations/salesforce
 * - Redirects unauthenticated users to /login
 * - Preserves Salesforce query parameters through all redirects
 *
 * @param isAuthenticated - Current authentication state
 * @param isAuthResolved - Whether the auth check has completed (prevents redirects during loading)
 */
export const useSalesforceIframeGuard = (
  isAuthenticated: boolean,
  isAuthResolved: boolean
) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait until auth state is settled before making redirect decisions
    if (!isAuthResolved) return;

    // Only activate guard when in Salesforce iframe context
    if (!isSalesforceIframe()) return;

    const currentPath = location.pathname;
    const salesforceParams = getSalesforceParams();

    // Allow navigation to permitted paths
    if (isSalesforceAllowedPath(currentPath)) return;

    // Redirect to appropriate page based on auth state
    const redirectPath = isAuthenticated
      ? `/pages/integrations/salesforce?${salesforceParams}`
      : `/login?${salesforceParams}`;

    console.warn(
      `[Salesforce Guard] Navigation to ${currentPath} blocked. Redirecting to ${redirectPath}`
    );

    navigate(redirectPath, { replace: true });
  }, [location.pathname, isAuthenticated, isAuthResolved, navigate]);
};
